/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from '../../registry/Manager';
import { page as SamplePage } from '../../help/sample_pages/SplitPanelPage';
import { preventIfParentPathContainsAutoLayout } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  Item,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { verticalSplitFixedSide } from '../../registry/constants';
import { defaultValue } from './HorizontalSplitPanel';

const MIN_WIDTH = 40;
const MAX_WIDTH = 1800;
const name = 'Horizontal Split Panel';

export const splitPanelChildTypes: ItemType[] = [
  'BingMap',
  'BingMapNavigator',
  'Box',
  'BoxContainer',
  'BoxList',
  'CalendarHeatmap',
  'CanvasGrid',
  'Card',
  'Chartjs',
  'Code',
  'CombinationChart',
  'CommentsPanel',
  'DataGrid',
  'DataList',
  'DataStoreReference',
  'EmbedPage',
  'FlexPanel',
  'Form',
  'GoogleMap',
  'HorizontalSplitPanel',
  'Markdown',
  'Mirror',
  'OCRComponent',
  'Paper',
  'RichTextEditor',
  'RowPanel',
  'SearchForm',
  'TabPanel',
  'VerticalSplitPanel',
  'WorkflowViewer',
] as ItemType[];

const HorizontalSplitPanelManager: ComponentManagerFn<
  'HorizontalSplitPanel'
> = (
  platform: Platform,
  type: 'HorizontalSplitPanel',
): ComponentManager<'HorizontalSplitPanel'> => {
  return {
    ...baseComponentManager<'HorizontalSplitPanel'>(
      platform,
      'HorizontalSplitPanel',
    ),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes(type),
          ...splitPanelChildTypes,
        ] as ItemType[]
      ).includes(childType) &&
      (item.children.length < 2 ? true : `${name} can only have two children!`),
    isAllowedInsideParent: (page, item) =>
      preventIfParentPathContainsAutoLayout(page, item, name),
    props: {
      fixedSide: {
        label: 'Fixed Side',
        type: 'select',
        options: verticalSplitFixedSide,
        required: true,
        category: 'Layout',
      },
      fixedHeightType: {
        label: 'Height Type',
        type: 'select',
        options: { px: 'Pixel', '%': 'Percent' },
        category: 'Layout',
      },
      fixedHeightPercent: {
        label: 'Height %',
        type: 'number',
        max: 95,
        min: 5,
        step: 5,
        category: 'Layout',
      },
      fixedHeight: {
        label: 'Height',
        type: 'number',
        max: MAX_WIDTH,
        min: MIN_WIDTH,
        step: 40,
        category: 'Layout',
      },
      fixedHeightMin: {
        label: 'Min Height',
        type: 'number',
        max: MAX_WIDTH,
        min: MIN_WIDTH,
        step: 40,
        category: 'Layout',
      },
      fixedHeightMax: {
        label: 'Max Height',
        type: 'number',
        max: MAX_WIDTH,
        min: MIN_WIDTH,
        step: 40,
        category: 'Layout',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Spacing',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'fixedHeightPercent':
          return itemData.fixedHeightType === '%';
        case 'fixedHeight':
          return itemData.fixedHeightType === 'px';
        default:
          return true;
      }
    },
    onReplace: (
      item: Item<'HorizontalSplitPanel'>,
      fromItem: Item<'VerticalSplitPanel'> | Item<'FlexPanel'>,
      page,
    ) => {
      if (fromItem.type === 'FlexPanel') {
        const children: string[] = [];
        fromItem.children.forEach((childId, index) => {
          const child = page[childId] as Item<'FlexItem'>;
          if (child.type === 'FlexItem') {
            if (child.props.sizeType === 'minmax' && child.props.max) {
              item.props.fixedHeight = child.props.max;
              item.props.fixedSide = index === 0 ? 'start' : 'end';
            }
            child.children.forEach((c) => {
              children.push(c);
              page[c] = { ...page[c], parent: item.itemId };
            });
          }
          delete page[childId];
        });
        item.children = children;
      } else if (fromItem.type === 'VerticalSplitPanel') {
        item.props.fixedHeightType = fromItem.props.fixedWidthType;
        item.props.fixedHeight = fromItem.props.fixedWidth;
        item.props.fixedHeightPercent = fromItem.props.fixedWidthPercent;
        item.props.fixedHeightMin = fromItem.props.fixedWidthMin;
        item.props.fixedHeightMax = fromItem.props.fixedWidthMax;
      }
      return item;
    },
    category: 'Containers',
    name,
    canHaveChildren: () => true,
    layout: 'fixed',
    icon: { iconName: 'border-center-h' },
    replaceableWith: ['VerticalSplitPanel', 'FlexPanel'],
    description: `**Overview**<br/>
The Horizontal Split Panel is a layout container that divides a single area into two vertically stacked sections (Top and Bottom). Unlike standard containers that simply stack items, the Split Panel provides a draggable divider, allowing users to interactively resize the height of each section to suit their viewing preference.
This component is ideal for master-detail interfaces where users need to see two related contexts simultaneously—such as a list of logs on top and the specific details of a selected log below.

**1. Defining the Anchor (Fixed Side)**<br/>
To ensure the layout remains stable when the browser window is resized, one side of the split panel must be designated as "Fixed."
* **Fixed Side:** Determines which section holds its initial size while the other stretches to fill the remaining space.
* **Start (Top):** The top section keeps its height; the bottom section expands.
* **End (Bottom):** The bottom section keeps its height; the top section expands.

**2. Controlling Height & Limits**<br/>
You can define precise constraints for the fixed section to prevent it from becoming too small (unusable) or too large (obscuring the other section).
* **Height Type:** Choose how the initial height is calculated. (Pixel sets a rigid static height; Percent sets a relative height based on available space).
* **Height:** The default size of the fixed section when the page loads.
* **Min Height / Max Height:** Defines the boundaries for resizing. The user cannot drag the divider past these points.
* **Note:** The system automatically enforces a safety minimum of 40px for both sections to prevent content from disappearing entirely.

**3. Content & Structure**<br/>
The Horizontal Split Panel is strictly designed for binary layouts.
* **Child Limit:** This component accepts exactly two children. (The first child becomes the Top section; the second child becomes the Bottom section).
* **Supported Content:** You can place any component inside these sections, but common choices include Data Grids, Charts, or Form Containers.
* **Layout Safety:** Because this component manages its own height calculations, it cannot be placed inside "Auto-Layout" parents that might conflict with its sizing logic.`,
    helpRenders: [
      {
        title: 'Horizontal Split Panel',
        description: `Adjust the various height props and see how it behaves. 

\`\`\`alert
Note: A minimum of 40px height will be maintained on either side irrespective of the min & max height configuration.
\`\`\`
`,
        page: SamplePage,
        exampleId: 'e1',
        height: 400,
        visibleProps: [
          'fixedHeightType',
          'fixedHeightPercent',
          'fixedHeightMin',
          'fixedHeight',
          'fixedHeightMax',
          'fixedSide',
        ],
        item: {
          itemId: 'horizontalSplitPanel',
          props: {
            fixedHeightMin: 40,
            fixedHeightMax: 1400,
            fixedHeight: 100,
          },
          children: ['paper1', 'paper'],
          type: 'HorizontalSplitPanel',
          parent: 'root',
        },
      },
    ],
  };
};

export default HorizontalSplitPanelManager;
